import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LANGUAGE_META, type LanguageCode } from '@/data/languages';
import { translations } from '@/data/translations';
import type { TranslationDictionary } from '@/data/translations';

type PersistOptions = {
  persist?: boolean;
};

type LanguageContextValue = {
  language: LanguageCode;
  translations: TranslationDictionary;
  setLanguage: (language: LanguageCode, options?: PersistOptions) => void;
  isBootstrapping: boolean;
};

const FALLBACK_LANGUAGE: LanguageCode = 'en';
const STORAGE_KEY = 'uiLanguage';

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const normalizeLanguage = (language: string | null | undefined): LanguageCode => {
  const candidate = String(language ?? '').split(/[-_]/)[0]?.toLowerCase();
  if (candidate && candidate in LANGUAGE_META) {
    return candidate as LanguageCode;
  }
  if (candidate === 'jp') {
    return 'ja';
  }
  return FALLBACK_LANGUAGE;
};

async function safeGetKV(key: string): Promise<string | null> {
  try {
    const { getKV } = await import('@/lib/services/kv');
    return (await getKV(key)) ?? null;
  } catch {
    return null;
  }
}

async function safeSetKV(key: string, value: string): Promise<void> {
  try {
    const { setKV } = await import('@/lib/services/kv');
    await setKV(key, value);
  } catch {
    // Ignore persistence failures
  }
}

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<LanguageCode>(FALLBACK_LANGUAGE);
  const [dictionary, setDictionary] = useState<TranslationDictionary>(translations[FALLBACK_LANGUAGE]);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const bootstrappedRef = useRef(false);
  const hasUserSetRef = useRef(false);

  const persistLanguagePreference = useCallback(async (nextLanguage: LanguageCode) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage?.setItem(STORAGE_KEY, nextLanguage);
      } catch {
        // Ignore storage errors (private mode etc.)
      }
    }
    await safeSetKV(STORAGE_KEY, nextLanguage);
  }, []);

  const applyLanguage = useCallback(
    (next: LanguageCode, options: PersistOptions = {}) => {
      const normalized = normalizeLanguage(next);
      setLanguageState(normalized);
      setDictionary(translations[normalized] || translations[FALLBACK_LANGUAGE]);
      if (options.persist !== false) {
        hasUserSetRef.current = true;
        void persistLanguagePreference(normalized);
      }
    },
    [persistLanguagePreference]
  );

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      if (bootstrappedRef.current) {
        return;
      }
      bootstrappedRef.current = true;

      let languageDetected = false;

      // Attempt localStorage first for immediate response
      if (typeof window !== 'undefined') {
        try {
          const stored = window.localStorage?.getItem(STORAGE_KEY);
          if (stored) {
            const normalized = normalizeLanguage(stored);
            applyLanguage(normalized, { persist: false });
            languageDetected = true;
          }
        } catch {
          // ignore
        }
      }

      // KV fallback (async) - only if localStorage didn't have a value
      if (!languageDetected) {
        const kvLanguage = await safeGetKV(STORAGE_KEY);
        if (!cancelled && kvLanguage && !hasUserSetRef.current) {
          applyLanguage(normalizeLanguage(kvLanguage), { persist: false });
          languageDetected = true;
        }
      }

      // If no stored language, detect from browser
      if (!cancelled && !languageDetected && typeof window !== 'undefined') {
        try {
          const browserLang = navigator.language.split('-')[0].toLowerCase();
          const normalized = normalizeLanguage(browserLang);
          // Apply browser language (even if it's English, to ensure consistency)
          applyLanguage(normalized, { persist: true });
          languageDetected = true;
        } catch {
          // ignore browser detection errors
        }
      }

      if (!cancelled) {
        setIsBootstrapping(false);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [applyLanguage, hasUserSetRef]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      translations: dictionary,
      setLanguage: applyLanguage,
      isBootstrapping
    }),
    [applyLanguage, dictionary, isBootstrapping, language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
